import { useTranslation } from "react-i18next";
import { Box, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Button, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { FS } from "@plannel/utils/font-util";

/*
 * 설정/구성 모달 공용 셸 — saas-web 표준.
 *
 * 규격(스킬 config-modal 과 동일 — sx 로 다시 칠하지 말 것)
 *   · Dialog     : borderRadius 2, 폭은 size 또는 width 로
 *   · 제목       : 20 / 700, pl 2 / pb 1
 *   · 구분선     : 제목 아래 + 액션 행 위, 각각 `<ModalRule />` (좌우 16px 인셋)
 *   · 액션       : 우측 정렬. 텍스트 Cancel(CloseIcon) + contained Save(SaveIcon)
 *                  부가 액션(Reset 등)이 있으면 좌측에 밀어 붙인다 — Cancel→Save 쌍은 우측 유지
 *
 * ⚠ 이 셸은 확인/알림 박스가 아니다. 단순 확인 다이얼로그는 components/Dialog.js 를 쓴다.
 *
 * props
 *   open, onClose : 열림/닫기
 *   title         : 제목(이미 번역된 문자열)
 *   onSave        : 저장 콜백. 없으면 저장 버튼 숨김(읽기 전용 설정 표시용)
 *   size          : "sm"(300) | "md"(360) | "lg"(500, ag-grid 등) — 기본 "md"
 *   width         : size 보다 우선(예외 케이스용). 숫자(px) 또는 CSS 문자열("60%", "min(1240px, 94vw)").
 *                   본문은 DialogContent px:2 안이라 **예전 Box 폭 + 32** 를 줘야 같은 폭이 나온다
 *   titleAdornment: 제목 오른쪽 끝에 붙는 노드(선택 개수 배지 등). 제목 규격은 그대로 둔다
 *   subtitle      : 제목 아래 한 줄 보조 설명(이미 번역된 문자열). 없으면 줄 자체가 없다
 *   paperSx       : ⚠ 탈출구. Paper 에 얹을 sx (폭이 애니메이션되는 모달의 transition 등).
 *                   **제목/강조선/액션/패딩 규격을 다시 칠하는 데 쓰지 말 것** — 그러라고 만든 게 아니다
 *   saveLabel     : 저장 버튼 라벨 키 대신 문자열을 직접 줄 때
 *   cancelLabel   : 취소 버튼 라벨. 기본 t("cancel").
 *                   닫으면서 부모를 갱신하는 등 "취소가 아닌 닫기"면 t("close") 를 준다
 *   saveIcon      : 저장 버튼 아이콘. 기본은 SaveIcon.
 *                   "생성"처럼 저장이 아닌 확정 동작이면 그 동작의 아이콘을 준다
 *                   (버전 생성 모달들은 <PlayArrowIcon /> — DP Process Mgmt 의 "Start DP" 와 맞춤)
 *   saveDisabled  : 입력이 유효하지 않아 저장을 막아야 할 때(예: 임계값이 정수가 아님)
 *   extraActions  : Cancel/Save 밖의 부가 액션 노드(예: Reset). 좌측 정렬로 들어간다.
 *                   액션이 아닌 좌측 상태 텍스트(표시 건수·범례)도 여기로 넣는다
 *   children      : 본문
 */

/*
 * 모달 구분선 — 제목 아래와 액션 행 위에 하나씩, **두 줄 다 같은 규격**이다.
 *   · 색   : 테마 `divider`(Divider 기본값). 디자인 원안의 #F0F4F5/#EDF1F2 는 너무 옅어 올렸다(2026-08-26).
 *   · 들여쓰기 : `mx: 2` — 본문 `px: 2` 와 같은 16px 이라, 본문 안에서 그은 선(툴바 hairline 등)과
 *     좌우 끝이 정확히 맞는다. **full-bleed 로 만들지 말 것** — 안쪽 선과 어긋나 "어떤 건 여백이 있고
 *     어떤 건 없는" 상태가 된다.
 * 값을 바꾸려면 여기 한 곳만 — 모달 셸 3개와 VersionCommonModal 이 전부 이걸 쓴다.
 */
export const ModalRule = () => <Divider sx={{ mx: 2 }} />;

/*
 * 본문 구역 제목 — 작은 트래킹 대문자(ui/TokenSelectGroup 의 섹션 제목과 같은 규격).
 * 본문 안에 **가로선을 새로 긋지 않고** 구역을 나누는 수단이다 — 셸 구분선과 나란한 선을 그으면
 * 스크롤바가 생겼을 때 그 선만 짧아져 어긋난다(위 ModalRule 주석 참고).
 * 값 목록 아래 옵션 체크박스를 묶을 때 쓴다(ExcelExportButton 의 Import, DpProcessMgmt 의 Start BF).
 */
export const MODAL_SECTION_LABEL_SX = {
  fontSize: FS.CAPTION,
  fontWeight: 600,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: "text.secondary",
  lineHeight: 1,
};

/*
 * 설정 모달 본문의 탭 규격. MUI 기본은 14px/48px 이라 본문(13px)보다 커서 탭 줄만 튄다.
 * <Tabs sx={CONFIG_TAB_SX}> 로 쓰고, 감싸는 Box 에 { borderBottom: 1, borderColor: "divider" } 를 준다.
 */
export const CONFIG_TAB_SX = {
  minHeight: 36,
  "& .MuiTab-root": { fontSize: FS.TITLE, fontWeight: 600, minHeight: 36, py: 0.5 },
};

// 폭 프리셋 — lg 는 ag-grid 를 품거나 제목이 긴 설정 모달용
const SIZE_WIDTH = { sm: 300, md: 360, lg: 500 };

/** title 이 없으면 Tooltip 을 씌우지 않는다 — 빈 툴팁이 뜨는 것을 막는다 */
const WithTooltip = ({ title, children }) =>
  title ? <Tooltip title={title}>{children}</Tooltip> : children;

const BaseConfigModal = ({
  open, onClose, title, onSave, size = "md", width, saveLabel, saveIcon, saveDisabled = false, extraActions,
  cancelLabel, titleAdornment, subtitle, paperSx, children,
  // 버튼이 하는 일이 라벨만으로 분명하지 않을 때 쓴다(예: 저장 vs 발행).
  // 주지 않으면 지금까지와 똑같이 툴팁 없이 그려진다.
  saveTooltip, cancelTooltip,
}) => {
  const { t } = useTranslation();
  const paperWidth = width ?? SIZE_WIDTH[size] ?? SIZE_WIDTH.md;

  return (
    <Dialog open={open} onClose={onClose}
      PaperProps={{ sx: { borderRadius: 2, width: paperWidth, maxWidth: "95vw", ...paperSx } }}>
      {/* 제목 줄 — 자식은 전부 span 이다. DialogTitle 이 <h2> 라 div 를 넣으면 마크업이 어긋난다 */}
      <DialogTitle sx={{ fontSize: FS.DISPLAY, fontWeight: 700, pl: 2, pb: 1 }}>
        <Box component="span" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
          <Box component="span">{title}</Box>
          {titleAdornment}
        </Box>
        {subtitle && (
          <Box component="span" sx={{ display: "block", mt: 0.5, fontSize: FS.SUB, fontWeight: 400, color: "text.secondary" }}>
            {subtitle}
          </Box>
        )}
      </DialogTitle>
      <ModalRule />
      {/* 상하좌우 16px 로 같다 — pt/pb 가 다르면 두 구분선 사이 내용이 한쪽으로 쏠려 보인다(지적받은 적 있음) */}
      <DialogContent sx={{ px: 2, py: 2 }}>
        {children}
      </DialogContent>
      <ModalRule />
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        {/* 부가 액션은 좌측으로 밀어 Cancel→Save 쌍이 우측에 붙어 있게 한다 */}
        {extraActions && <Box sx={{ mr: "auto", display: "flex", gap: 1 }}>{extraActions}</Box>}
        <WithTooltip title={cancelTooltip}>
          <Button size="small" startIcon={<CloseIcon />} onClick={onClose}>{cancelLabel ?? t("cancel")}</Button>
        </WithTooltip>
        {onSave && (
          <WithTooltip title={saveTooltip}>
            {/* disabled 버튼은 이벤트를 내지 않아 Tooltip 이 안 뜬다 — span 으로 감싼다 */}
            <Box component="span">
              <Button size="small" variant="contained" disableElevation startIcon={saveIcon ?? <SaveIcon />}
                sx={{ minWidth: 72 }} disabled={saveDisabled} onClick={onSave}>
                {saveLabel ?? t("save")}
              </Button>
            </Box>
          </WithTooltip>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BaseConfigModal;
